import { useEffect, useState } from 'react';
import {
  addDoc, collection, deleteDoc, doc, onSnapshot, query, serverTimestamp, updateDoc, where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from './Toast';
import ConfirmDialog from './ConfirmDialog';
import Modal from './Modal';

// Tour → Letters → Tour Contract. Staff send the Sports Tour Contract for the
// tour contact to e-sign; once signed, an admin countersigns with Michael's
// saved signature and the signed PDF lands in Sent Letters. Until MST switch
// signing on (Admin Settings → Contract Signing) only admins see this.
// Backend: onContractSendRequested / onContractUpdated in functions/index.js.

const STATUS = {
  sent:           { label: 'Awaiting customer signature', cls: 'bg-[#FEF3C7] text-[#92400E]' },
  signed:         { label: 'Awaiting MST countersignature', cls: 'bg-[#DBEAFE] text-[#1E40AF]' },
  countersigning: { label: 'Countersigning…', cls: 'bg-[#DBEAFE] text-[#1E40AF]' },
  completed:      { label: 'Signed by both parties', cls: 'bg-[#DCFCE7] text-[#166534]' },
  void:           { label: 'Withdrawn', cls: 'bg-[#F3F4F6] text-[#6B7280]' },
};

const CURRENCY = { GBP: '£', EUR: '€', USD: '$' };

function when(ts) {
  const d = ts?.toDate ? ts.toDate() : null;
  return d ? d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
}

const inputCls = 'w-full border border-[#E5E7EB] rounded-[9px] px-3 py-[9px] text-[13px] focus:outline-none focus:ring-2 focus:ring-pb';
const labelCls = 'block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-[6px]';

export default function ContractSigning({ requestId, request }) {
  const { currentUser, isAdmin } = useAuth();
  const toast = useToast();
  const [live, setLive] = useState(false);
  const [signatory, setSignatory] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [recipientName, setRecipientName] = useState(request.contactName || '');
  const [recipientEmail, setRecipientEmail] = useState(request.customerEmail || '');
  const [organisation, setOrganisation] = useState(request.groupName || request.internalRef || '');
  const [sending, setSending] = useState(false);
  const [confirm, setConfirm] = useState(null); // { kind: 'countersign' | 'void', contract }
  const [busy, setBusy] = useState(false);
  const [payments, setPayments] = useState([]);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null); // { downloadUrl, emailSubject, emailHtml, to }

  useEffect(() => onSnapshot(doc(db, 'contractSettings', 'main'),
    (s) => setLive(s.exists() && s.data().enabled === true), () => setLive(false)), []);

  useEffect(() => {
    if (!isAdmin) return undefined;
    return onSnapshot(doc(db, 'contractSettings', 'signatory'),
      (s) => setSignatory(s.exists() ? s.data() : null), () => setSignatory(null));
  }, [isAdmin]);

  useEffect(() => onSnapshot(query(collection(db, 'contracts'), where('requestId', '==', requestId)), (snap) => {
    const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    rows.sort((a, b) => (b.sentAt?.toMillis?.() || Infinity) - (a.sentAt?.toMillis?.() || Infinity));
    setContracts(rows);
  }, (err) => console.error('Contracts listener failed:', err)), [requestId]);

  // Preview of the payment schedule the contract will carry — the backend
  // reads the same invoices when it's sent (onContractSendRequested).
  useEffect(() => onSnapshot(query(collection(db, 'invoices'), where('requestId', '==', requestId)), (snap) => {
    setPayments(snap.docs.map((d) => d.data())
      .filter((inv) => inv.invoiceType !== 'match_ticket' && inv.dueDate)
      .sort((a, b) => a.dueDate.localeCompare(b.dueDate) || (a.instalmentNumber || 0) - (b.instalmentNumber || 0)));
  }, (err) => console.error('Invoices listener failed:', err)), [requestId]);

  if (!live && !isAdmin) return null;

  // What the contract will be filled with — mirrors buildContractContent()
  // in functions/index.js. Missing details are dropped from the contract.
  const ukDate = (d) => (d ? d.split('-').reverse().join('/') : '');
  const tourLabel = request.tourName || [request.sport, request.destination].filter(Boolean).join(' — ');
  const fields = [
    {
      label: 'Organisation (customer party)', source: 'Edit above',
      value: organisation.trim(),
      status: organisation.trim() ? 'ok' : 'missing',
      note: organisation.trim()
        ? (request.groupName || request.internalRef ? '' : 'Typed in above — the tour has no group name.')
        : 'Required. Named as the customer party in the contract — the contract can\'t be sent without it.',
    },
    {
      label: 'Group Leader', source: 'Edit above',
      value: recipientName.trim(),
      status: recipientName.trim() ? 'ok' : 'missing',
      note: recipientName.trim() ? '' : 'Missing — the contact fills in their own name when signing.',
    },
    {
      label: 'Tour', source: 'Overview tab',
      value: tourLabel,
      status: tourLabel ? 'ok' : 'missing',
      note: tourLabel ? '' : 'Missing — add a tour name on the Overview tab.',
    },
    {
      label: 'Travel dates', source: 'Overview tab',
      value: [request.departureDate, request.returnDate].filter(Boolean).map(ukDate).join(' – '),
      status: request.departureDate && request.returnDate ? 'ok' : (request.departureDate || request.returnDate ? 'partial' : 'missing'),
      note: request.departureDate && request.returnDate ? ''
        : request.departureDate ? 'No return date — only the departure date will show.'
        : request.returnDate ? 'No departure date — only the return date will show.'
        : 'Missing — add the travel dates on the Overview tab.',
    },
    {
      label: 'Participants', source: 'Overview tab',
      value: request.participants ? String(request.participants) : '',
      status: request.participants ? 'ok' : 'missing',
      note: request.participants ? '' : 'Missing — add the group size on the Overview tab.',
    },
    {
      label: 'Booking reference', source: 'Overview tab',
      value: request.internalRef || requestId,
      status: request.internalRef ? 'ok' : 'partial',
      note: request.internalRef ? '' : 'No internal reference — the portal\'s tour ID will be used instead.',
    },
    {
      label: 'Payment schedule', source: 'Quote tab → invoices',
      value: payments.map((p, i) => `${p.instalmentLabel || `Instalment ${p.instalmentNumber || i + 1}`} — ${CURRENCY[p.currency] || '£'}${Number(p.amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} due ${ukDate(p.dueDate)}`).join('\n'),
      status: payments.length ? 'ok' : 'missing',
      note: payments.length ? 'Match-ticket invoices are left out (payable on demand under clause 4).'
        : 'No invoices yet — the contract will only carry clause 4\'s standard schedule. Generate the invoices on the Quote tab first.',
    },
  ];
  const missingCount = fields.filter((f) => f.status !== 'ok').length;

  const openContract = contracts.find((c) => c.status === 'sent');
  const emailOk = /\S+@\S+\.\S+/.test(recipientEmail.trim());

  async function handleSend() {
    if (!emailOk || sending) return;
    setSending(true);
    try {
      const ref = await addDoc(collection(db, 'contractSendRequests'), {
        requestId,
        recipientEmail: recipientEmail.trim(),
        recipientName: recipientName.trim(),
        organisation: organisation.trim(),
        requestedBy: currentUser.uid,
        requestedByEmail: currentUser.email || null,
        requestedAt: serverTimestamp(),
      });
      const unsub = onSnapshot(ref, (s) => {
        const st = s.data()?.status;
        if (!st) return;
        unsub();
        setSending(false);
        if (st === 'sent') toast(`Contract sent to ${recipientEmail.trim()}`);
        else toast(`Contract not sent: ${s.data().error || 'unknown error'}`, 'error');
      });
    } catch (err) {
      console.error(err);
      setSending(false);
      toast('Could not send the contract', 'error');
    }
  }

  // Renders the contract exactly as it would be sent (same details, payment
  // schedule and wording, unsigned) plus the invitation email.
  async function handlePreview() {
    if (previewing) return;
    setPreviewing(true);
    let ref = null;
    try {
      ref = await addDoc(collection(db, 'contractPreviewRequests'), {
        requestId,
        recipientName: recipientName.trim(),
        organisation: organisation.trim(),
        requestedBy: currentUser.uid,
        requestedAt: serverTimestamp(),
      });
      const result = await new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => { unsub(); reject(new Error('Preview timed out. Please try again.')); }, 45000);
        const unsub = onSnapshot(ref, (snap) => {
          const d = snap.data();
          if (!d?.status) return;
          clearTimeout(timeoutId);
          unsub();
          if (d.status === 'ready') resolve(d);
          else reject(new Error(d.error || 'Could not generate a preview.'));
        }, (err) => { clearTimeout(timeoutId); unsub(); reject(err); });
      });
      setPreview({ ...result, to: recipientEmail.trim() });
    } catch (err) {
      console.error('Contract preview failed:', err);
      toast(err.message || 'Could not generate a preview', 'error');
    } finally {
      setPreviewing(false);
      if (ref) deleteDoc(ref).catch(() => {});
    }
  }

  async function handleConfirm() {
    const { kind, contract } = confirm;
    setBusy(true);
    try {
      const ref = doc(db, 'contracts', contract.id);
      if (kind === 'countersign') {
        await updateDoc(ref, { status: 'countersigning', countersignRequestedBy: currentUser.uid, countersignRequestedAt: serverTimestamp() });
        toast('Countersigned — the signed contract is being emailed out');
      } else {
        await updateDoc(ref, { status: 'void', voidedBy: currentUser.uid, voidedAt: serverTimestamp() });
        toast('Contract withdrawn');
      }
      setConfirm(null);
    } catch (err) {
      console.error(err);
      toast(kind === 'countersign' ? 'Could not countersign' : 'Could not withdraw the contract', 'error');
    } finally {
      setBusy(false);
    }
  }

  function copyLink(c) {
    navigator.clipboard.writeText(`${window.location.origin}/sign-contract?token=${c.id}`)
      .then(() => toast('Signing link copied'), () => toast('Could not copy the link', 'error'));
  }

  const hasSignature = !!signatory?.signatureImage && !!signatory?.name;

  return (
    <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] p-[18px]">
      <div className="flex items-start justify-between gap-3 mb-[14px]">
        <div>
          <h3 className="text-[14.5px] font-bold text-[#111827]">Tour Contract</h3>
          <p className="text-[12px] text-[#9CA3AF] mt-0.5">The tour contact e-signs the Sports Tour Contract, then MST countersigns.</p>
        </div>
        {!live && (
          <span className="flex-shrink-0 text-[11px] font-bold px-2.5 py-1 rounded-full bg-[#FEF3C7] text-[#92400E]"
            title="Only admins can see and send contracts until signing is switched on in Admin Settings → Contract Signing">
            Not live — admins only
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className={labelCls}>Contact name</label>
          <input value={recipientName} onChange={(e) => setRecipientName(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Contact email</label>
          <input type="email" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Organisation (customer party)</label>
          <input value={organisation} onChange={(e) => setOrganisation(e.target.value)} placeholder="Club / school name" className={inputCls} />
        </div>
      </div>
      <div className="mt-3 rounded-[10px] bg-[#F9FAFB] border border-[#E5E7EB] px-4 py-3">
        <div className="flex items-center justify-between gap-3 mb-2">
          <p className="text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px]">What goes into the contract</p>
          <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${missingCount ? 'bg-[#FEF3C7] text-[#92400E]' : 'bg-[#DCFCE7] text-[#166534]'}`}>
            {missingCount ? `${missingCount} to check` : 'All details found'}
          </span>
        </div>
        <ul className="divide-y divide-[#EEF0F3]">
          {fields.map((f) => (
            <li key={f.label} className="flex items-start gap-2.5 py-1.5">
              <span className={`mt-[3px] w-4 h-4 flex-shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold ${
                f.status === 'ok' ? 'bg-[#DCFCE7] text-[#166534]' : f.status === 'partial' ? 'bg-[#FEF3C7] text-[#92400E]' : 'bg-[#FEE2E2] text-[#991B1B]'
              }`} aria-hidden>
                {f.status === 'ok' ? '✓' : '!'}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <span className="text-[12.5px] font-semibold text-[#111827]">{f.label}</span>
                  <span className="text-[11px] text-[#9CA3AF]">{f.source}</span>
                </div>
                {f.value && <p className="text-[12.5px] text-[#374151] break-words whitespace-pre-line">{f.value}</p>}
                {f.note && <p className={`text-[12px] ${f.status === 'ok' ? 'text-[#9CA3AF]' : 'text-[#92400E]'}`}>{f.note}</p>}
              </div>
            </li>
          ))}
        </ul>
        <p className="text-[11.5px] text-[#9CA3AF] mt-1.5">
          Fixed when the contract is sent. Missing details are left out of the contract; fix them on the tour, then send.
        </p>
      </div>

      <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
        <p className="text-[12px] text-[#9CA3AF]">
          {openContract ? `Sending again withdraws the unsigned contract sent ${when(openContract.sentAt)}.` : 'The contact gets an email with a link to read and sign the contract.'}
        </p>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={handlePreview} disabled={previewing || !organisation.trim()}
            className="px-[18px] py-[9px] text-[13px] font-bold text-[#374151] bg-white border border-[#E5E7EB] hover:bg-[#F9FAFB] rounded-[9px] transition disabled:opacity-60">
            {previewing ? 'Preparing…' : 'Preview'}
          </button>
          <button onClick={handleSend} disabled={!emailOk || sending || !organisation.trim()}
            className="px-[18px] py-[9px] text-[13px] font-bold text-white bg-pb hover:bg-navy rounded-[9px] transition disabled:opacity-60">
            {sending ? 'Sending…' : 'Send for signature'}
          </button>
        </div>
      </div>

      {contracts.length > 0 && (
        <div className="mt-4 pt-4 border-t border-[#F1F3F6] space-y-2.5">
          {contracts.map((c) => {
            const st = STATUS[c.status] || STATUS.sent;
            return (
              <div key={c.id} className={`rounded-[10px] border border-[#E5E7EB] px-4 py-3 ${c.status === 'void' ? 'opacity-60' : ''}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <span className={`inline-block text-[11px] font-bold px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                    <p className="text-[12.5px] text-[#374151] mt-1.5">
                      Sent to <strong>{c.sentTo}</strong>{c.sentAt ? ` · ${when(c.sentAt)}` : ''}
                      {c.viewedAt && c.status === 'sent' ? ` · opened ${when(c.viewedAt)}` : ''}
                    </p>
                    {c.signer && (
                      <p className="text-[12.5px] text-[#374151]">
                        Signed by <strong>{c.signer.name}</strong>{c.signer.position ? `, ${c.signer.position}` : ''} · {when(c.signedAt)}
                      </p>
                    )}
                    {c.countersigner && (
                      <p className="text-[12.5px] text-[#374151]">
                        Countersigned by <strong>{c.countersigner.name}</strong> · {when(c.countersignedAt)}
                      </p>
                    )}
                    {c.error && c.status === 'signed' && <p className="text-[12.5px] text-dr mt-1">{c.error}</p>}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {c.status === 'sent' && (
                      <button onClick={() => copyLink(c)}
                        className="px-3 py-1.5 text-[12px] font-bold text-[#374151] bg-white border border-[#E5E7EB] hover:bg-[#F9FAFB] rounded-[8px]">
                        Copy link
                      </button>
                    )}
                    {c.status === 'signed' && isAdmin && (
                      <button onClick={() => setConfirm({ kind: 'countersign', contract: c })} disabled={!hasSignature}
                        title={hasSignature ? '' : 'Add the countersignature in Admin Settings → Contract Signing first'}
                        className="px-3 py-1.5 text-[12px] font-bold text-white bg-[#16A34A] hover:bg-[#15803D] rounded-[8px] disabled:opacity-50">
                        {hasSignature ? `Countersign as ${signatory.name}` : 'Countersign (no signature saved)'}
                      </button>
                    )}
                    {c.status === 'signed' && !isAdmin && (
                      <span className="text-[12px] text-[#6B7280]">An admin countersigns this</span>
                    )}
                    {(c.status === 'sent' || c.status === 'signed') && (
                      <button onClick={() => setConfirm({ kind: 'void', contract: c })}
                        className="px-3 py-1.5 text-[12px] font-bold text-dr bg-white border border-[#E5E7EB] hover:bg-[#FEF2F2] rounded-[8px]">
                        Withdraw
                      </button>
                    )}
                    {c.status === 'completed' && c.downloadUrl && (
                      <a href={c.downloadUrl} target="_blank" rel="noreferrer"
                        className="px-3 py-1.5 text-[12px] font-bold text-white bg-pb hover:bg-navy rounded-[8px]">
                        Download PDF
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal open={!!preview} onClose={() => setPreview(null)} title="Preview — nothing has been sent" maxWidth="max-w-2xl">
        {preview && (
          <div className="px-6 py-5 overflow-y-auto space-y-4">
            <div className="flex items-center justify-between gap-3 rounded-[10px] border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3">
              <div>
                <p className="text-[13px] font-bold text-[#111827]">The contract</p>
                <p className="text-[12px] text-[#6B7280]">Unsigned, exactly as the contact will be asked to sign it.</p>
              </div>
              <a href={preview.downloadUrl} target="_blank" rel="noreferrer"
                className="px-4 py-2 text-[12.5px] font-bold text-white bg-pb hover:bg-navy rounded-[8px] flex-shrink-0">
                Open contract PDF
              </a>
            </div>
            <div>
              <p className="text-[13px] font-bold text-[#111827]">The email</p>
              <p className="text-[12px] text-[#6B7280] mb-2">
                To <strong>{preview.to || '—'}</strong> · Subject: {preview.emailSubject}
              </p>
              <iframe title="Email preview" srcDoc={preview.emailHtml} sandbox=""
                className="w-full h-[420px] rounded-[10px] border border-[#E5E7EB] bg-white" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setPreview(null)}
                className="px-4 py-2 text-[13px] font-bold text-[#374151] bg-white border border-[#E5E7EB] hover:bg-[#F9FAFB] rounded-[9px]">
                Close
              </button>
              <button onClick={() => { setPreview(null); handleSend(); }} disabled={!emailOk || sending}
                className="px-4 py-2 text-[13px] font-bold text-white bg-pb hover:bg-navy rounded-[9px] disabled:opacity-60">
                Send for signature
              </button>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.kind === 'countersign' ? 'Countersign this contract?' : 'Withdraw this contract?'}
        message={confirm?.kind === 'countersign'
          ? `This adds ${signatory?.name || 'the saved'}'s signature for MST Sports Tours, alongside ${confirm.contract.signer?.name}'s, and emails the signed PDF to ${confirm.contract.sentTo}.`
          : 'The signing link will stop working. You can send a new contract afterwards.'}
        confirmLabel={confirm?.kind === 'countersign' ? 'Sign' : 'Withdraw'}
        danger={confirm?.kind !== 'countersign'}
        loading={busy}
        onConfirm={handleConfirm}
        onCancel={() => !busy && setConfirm(null)}
      />
    </div>
  );
}
